import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Skeleton } from '@/components/ui/skeleton';
import { useCpvCodes } from '../hooks/use-dossier-company';

/**
 * Sprint 5b.1 v2 — Phase C.2 : picker multi-tags pour codes CPV.
 *
 * Multi-sélection construit sur le `Combobox` (single-select sous-jacent) :
 *   - Le `Combobox` propose les CPV NON déjà sélectionnés
 *   - Sélection → ajout au tableau `value`
 *   - Chaque tag affiche le code + libellé court avec bouton de retrait
 *
 * Consommé par :
 *   - Bloc 2 (extensions `targetSectorsCpv`)
 *   - Bloc 3 (exclusions `excludedSectorsCpv`)
 *   - Section Références (`aoWonSectorsCpv`)
 */
export interface CpvPickerProps {
  value: string[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
  emptyTagsMessage?: string;
  disabled?: boolean;
  /** Codes à exclure du picker (ex. ceux du bloc opposé pour éviter conflit) */
  excludeCodes?: string[];
}

export function CpvPicker({
  value,
  onChange,
  placeholder = 'Ajouter un code CPV...',
  emptyTagsMessage = 'Aucun code sélectionné',
  disabled,
  excludeCodes,
}: CpvPickerProps) {
  const { data: cpvCodes, isLoading } = useCpvCodes();

  const options: ComboboxOption[] = useMemo(() => {
    if (!cpvCodes) return [];
    const selectedSet = new Set(value);
    const excludedSet = new Set(excludeCodes ?? []);
    return cpvCodes
      .filter((c) => !selectedSet.has(c.code) && !excludedSet.has(c.code))
      .map((c) => ({
        value: c.code,
        label: c.label ?? c.subSector ?? c.sector,
        hint: c.sector,
        keywords: [c.sector, c.subSector ?? '', c.label ?? ''].filter(Boolean),
      }));
  }, [cpvCodes, value, excludeCodes]);

  const tagLabels = useMemo(() => {
    if (!cpvCodes) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const c of cpvCodes) {
      map.set(c.code, c.label ?? c.subSector ?? c.sector);
    }
    return map;
  }, [cpvCodes]);

  function handleSelect(code: string | undefined) {
    if (!code) return;
    if (value.includes(code)) return;
    onChange([...value, code]);
  }

  function handleRemove(code: string) {
    onChange(value.filter((c) => c !== code));
  }

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <div className="space-y-2">
      <Combobox
        options={options}
        value={undefined}
        onChange={handleSelect}
        placeholder={placeholder}
        searchPlaceholder="Rechercher par code ou intitulé..."
        emptyMessage="Aucun code CPV trouvé."
        disabled={disabled}
      />
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{emptyTagsMessage}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1.5 rounded-md border bg-secondary px-2 py-1 text-xs"
            >
              <span className="font-mono text-muted-foreground">{code}</span>
              <span className="max-w-[200px] truncate">{tagLabels.get(code) ?? '—'}</span>
              <button
                type="button"
                onClick={() => handleRemove(code)}
                className="ml-0.5 rounded hover:bg-destructive/20 hover:text-destructive"
                aria-label={`Retirer ${code}`}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
